import { useAuth } from "@clerk/nextjs";
import { ButtonVariant, CoButton } from "components/CoButton";
import Dropdown from "components/CoDropdown";
import { CoDropdownItemProps } from "components/CoDropdown/CoDropdown";
import { HumanFeedbackModal } from "components/HumanFeedbackModal";
import { ShareDropdown } from "components/ShareDropdown";
import Tooltip from "components/Tooltip";
import { DOI_URL } from "constants/config";
import {
  FullPaperLibraryLink,
  LibraryIcon,
  useFullPaperLibraryLink,
} from "features/libraries";
import { useResearchPaperUpload } from "features/ResearchHub/hooks/useResearchPaperUpload";
import useUploadedPapers from "features/ResearchHub/hooks/useUploadedPapers";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper, PaperDetails, convertPaperDetailsToPaper } from "helpers/api";
import { addUTMToLink } from "helpers/pageUrl";
import {
  MixpanelActionSource,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { usePdfAccessHierarchy } from "hooks/usePdfAccessHierarchy";
import { ArrowUpRight } from "lucide-react";
import { useRouter } from "next/router";
import React, { ReactElement, ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDetailContent } from "./DetailContentContext";
import ManagePdfButton from "./PaperAccessButton/ManagePdfButton";
import OpenAccessIcon from "design_system/color_icons/OpenAccessIcon";

const REDIRECT_ON_UPLOAD_DELAY_MS = 1000;

interface PaperLinksProps {
  handleClickFullTextLink: (linkType: MixpanelFullTextLinkType) => void;
  handleClickSave?: (paper: Paper) => void;
  handleClickCite?: () => void;
  handleClickShare?: () => void;
  isBookmarked?: boolean;
  paper: PaperDetails;
  hideSemanticScholarButton?: boolean;
  onDelete?: () => void;
}

interface PaperButtonProps {
  variant: ButtonVariant;
  onClick: () => void;
  href?: string;
  icon?: string | ReactElement;
  iconRight?: string | ReactElement;
  label: string;
  className?: string;
}

const PaperButton: React.FC<PaperButtonProps> = ({
  variant,
  onClick,
  href,
  icon,
  iconRight,
  label,
  className,
}) => (
  <CoButton
    variant={variant}
    size="sm"
    onClick={onClick}
    href={addUTMToLink(href)}
    target="_blank"
    icon={icon}
    iconRight={iconRight}
    label={label}
    className={className}
  />
);

export const PaperLinks = ({
  handleClickFullTextLink,
  handleClickSave,
  handleClickCite,
  handleClickShare,
  isBookmarked,
  paper,
  hideSemanticScholarButton,
  onDelete,
}: PaperLinksProps) => {
  const [
    labels,
    giveFeedbackLabel,
    fullTextLabel,
    semanticScholarLabel,
    feedbackConfirmationText,
    shareLabels,
    detailPageLabels,
  ] = useLabels(
    "ask-paper-upload-new-paper-guide",
    "screens.details.give-feedback-text",
    "screens.details.full-text",
    "screens.details.semantic-scholar",
    "human-feedback-modal.thanks-for-feedback",
    "share",
    "screens.details"
  );
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [detailContent] = useDetailContent();
  const [hasUserUploadedPaper, setHasUserUploadedPaper] = useState(false);
  const [uploadedPaper, setUploadedPaper] = useState<PaperDetails | null>(null);
  const { papers } = useUploadedPapers();
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();
  const { triggerFileSelection, isUploading } = useResearchPaperUpload({
    onSuccess: (url?: string) => {
      toast.success(labels["toast-success"]);
      setTimeout(() => {
        if (url) {
          router.push(url);
        }
      }, REDIRECT_ON_UPLOAD_DELAY_MS);
    },
    onError: (errorMessage?: string) => {
      toast.error(errorMessage || labels["toast-error"]);
    },
    actionSource: MixpanelActionSource.DetailsPage,
    force_hash_paper_id: paper.id,
  });

  const closeFeedbackModal = () => setShowFeedbackModal(false);
  const openFeedbackModal = () => setShowFeedbackModal(true);
  const openFeedbackSuccess = () => {
    setShowFeedbackModal(false);
    toast.success(feedbackConfirmationText);
  };

  useEffect(() => {
    const uploadedPaper = papers.find(
      (uploaded_paper) => uploaded_paper.paper_id === paper.id
    );
    if (uploadedPaper) {
      setHasUserUploadedPaper(true);
      setUploadedPaper(uploadedPaper as unknown as PaperDetails);
    }
  }, [papers, paper.id]);

  const isUserUploadedPaper = hasUserUploadedPaper;
  const { library_id } = useUserSettings();

  // Get LibKey access if available
  const { data: fullPaperLibraryLink } = useFullPaperLibraryLink(
    library_id,
    paper.doi,
    Boolean(library_id && paper.doi)
  );

  const {
    option: pdfAccessOption,
    icon: pdfAccessIcon,
    url: pdfAccessUrl,
  } = usePdfAccessHierarchy(
    paper,
    fullTextLabel,
    detailPageLabels["pdf"],
    isUserUploadedPaper
  );

  // Build menu items for overflow menu
  const menuItems: CoDropdownItemProps[] = [];

  // Always show LibKey in overflow menu if available and not the primary action
  if (pdfAccessOption !== "library" && fullPaperLibraryLink) {
    menuItems.push({
      onClick: () => handleClickFullTextLink(MixpanelFullTextLinkType.LIBRARY),
      href: fullPaperLibraryLink,
      target: "_blank",
      leftIcon: (
        <>
          <ArrowUpRight size={16} className="text-xl icon-external-link" />
          <LibraryIcon includeTooltip={false} />
        </>
      ),
      title: detailPageLabels["institution-access-pdf"],
      key: "library",
    });
  }

  // Always show open access in overflow menu if available and not the primary action
  if (pdfAccessOption !== "open-access" && paper.open_access_pdf_url) {
    menuItems.push({
      onClick: () => handleClickFullTextLink(MixpanelFullTextLinkType.PDF),
      href: addUTMToLink(paper.open_access_pdf_url),
      target: "_blank",
      leftIcon: (
        <>
          <ArrowUpRight size={16} className="text-xl icon-external-link" />
          <div className="relative p-1 bg-bg-base rounded-lg overflow-hidden flex items-center justify-center">
            <OpenAccessIcon size="sm" />
          </div>
        </>
      ),
      title: detailPageLabels["open-access-pdf"],
      key: "open-access",
    });
  }

  // Always add Semantic Scholar to overflow menu
  if (!hideSemanticScholarButton) {
    menuItems.push({
      onClick: () => handleClickFullTextLink(MixpanelFullTextLinkType.S2),
      href: addUTMToLink(paper.provider_url),
      target: "_blank",
      leftIcon: (
        <ArrowUpRight size={16} className="text-xl icon-external-link" />
      ),
      title: semanticScholarLabel,
      key: "semantic-scholar",
    });
  }

  // Add Upload PDF to menu items (only if no paper is uploaded)
  if (!isUserUploadedPaper) {
    menuItems.push({
      onClick: triggerFileSelection,
      leftIcon: <i className="text-xl icon-upload" />,
      title: isUploading ? labels["uploading"] : labels["upload-paper"],
      key: "upload-pdf",
    });
  }

  // Add Give feedback to menu items (always in overflow)
  menuItems.push({
    onClick: openFeedbackModal,
    leftIcon: <i className="text-xl icon-flag" />,
    title: giveFeedbackLabel,
    key: "give-feedback",
  });

  // Render main action bar
  const renderMainActions = (): ReactNode => {
    const actions: ReactNode[] = [];

    // 1. PDF access button (only one based on priority)
    if (pdfAccessOption === "uploaded") {
      actions.push(
        <ManagePdfButton
          key="manage-pdf"
          uploaded_paper_id={uploadedPaper?.id}
          isUploading={false}
          onDelete={onDelete}
        />
      );
    } else if (pdfAccessOption === "library") {
      actions.push(
        <FullPaperLibraryLink
          key="library"
          item={paper}
          actionSource={MixpanelActionSource.DetailsPage}
        />
      );
    } else if (pdfAccessOption === "open-access") {
      actions.push(
        <PaperButton
          key="open-access"
          variant={ButtonVariant.Secondary}
          onClick={() => handleClickFullTextLink(MixpanelFullTextLinkType.PDF)}
          href={pdfAccessUrl}
          icon={<OpenAccessIcon />}
          iconRight={<ArrowUpRight size={16} />}
          label={detailPageLabels["pdf"]}
        />
      );
    } else if (pdfAccessOption === "doi") {
      actions.push(
        <PaperButton
          key="doi"
          variant={ButtonVariant.Secondary}
          onClick={() => handleClickFullTextLink(MixpanelFullTextLinkType.S2)}
          href={pdfAccessUrl}
          iconRight={<ArrowUpRight size={16} />}
          label={fullTextLabel}
        />
      );
    } else {
      // No PDF access available - show disabled button
      actions.push(
        <CoButton
          key="no-access"
          variant={ButtonVariant.Tertiary}
          icon="icon-external-link"
          label={fullTextLabel}
          size="sm"
          disabled={true}
        />
      );
    }

    // 2. Save button
    if (handleClickSave) {
      actions.push(
        <CoButton
          key="save"
          icon={isBookmarked ? "icon-bookmark-solid" : "icon-bookmark"}
          label={
            isBookmarked ? detailPageLabels["saved"] : detailPageLabels["save"]
          }
          onClick={() => handleClickSave(convertPaperDetailsToPaper(paper))}
          size="sm"
          variant={ButtonVariant.Tertiary}
          className="ml-2"
          disabled={!paper}
        />
      );
    }

    // 3. Cite button
    if (handleClickCite) {
      actions.push(
        <CoButton
          key="cite"
          hideLabelResponsively
          icon="icon-quote"
          label={detailPageLabels["cite"]}
          onClick={handleClickCite}
          size="sm"
          variant={ButtonVariant.Flat}
          className="ml-2"
          disabled={!paper}
        />
      );
    }

    // 4. Share button
    if (handleClickShare) {
      actions.push(
        <ShareDropdown
          key="share"
          title={shareLabels["share-paper"]}
          description={shareLabels["share-paper-details-description"]}
          keyTakeaway={paper?.abstract_takeaway}
        >
          <CoButton
            data-testid="share-detail"
            hideLabelResponsively
            icon="icon-link"
            onClick={handleClickShare}
            size="sm"
            variant={ButtonVariant.Flat}
            className="ml-2"
            disabled={!paper}
          />
        </ShareDropdown>
      );
    }

    return actions;
  };

  return (
    <div className="flex justify-between w-full w-">
      <div className="flex">{renderMainActions()}</div>
      <div className="z-10 flex">
        {menuItems.length > 0 && (
          <Dropdown
            items={menuItems}
            disabledKeys={!isSignedIn || !isLoaded ? ["upload-pdf"] : []}
          >
            <Tooltip content={detailPageLabels["more-tooltip"]}>
              <CoButton
                variant={ButtonVariant.Flat}
                icon="icon-ellipsis"
                data-testid="additional-links-menu"
              />
            </Tooltip>
          </Dropdown>
        )}
        <HumanFeedbackModal
          paper={paper}
          open={showFeedbackModal}
          onClose={closeFeedbackModal}
          studyDetails={detailContent?.studyDetails}
          onSubmitSuccess={openFeedbackSuccess}
        />
      </div>
    </div>
  );
};
